import React, { useMemo } from 'react';
import { Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { StarFilled, StarHalfFilled } from '@withjoy/joykit/icons';

import { useTranslation } from '@shared/core';
import { Skeleton, SkeletonText } from '@shared/components/Skeleton';
import { formatUserRatingNumber, useReviewCountFormatter } from '@shared/utils/accommodations/AccommodationTiles';

const DEFAULT_MIN_REVIEW_COUNT = 10;

type UserRatingProps = Readonly<{
  rating: number | null | undefined;
  reviewCount: number | null | undefined;
  minReviewCount?: number;
  /**
   * Align the stacked label and review count to a common left edge, instead of leaving them to the
   * default.
   *
   * Opt-in rather than the default because the existing value is `none` — which is NOT a valid
   * `align-items` value, so it falls back to `normal`/`stretch` and nothing is aligned at all. That
   * is a real defect, but it is the behaviour thirteen components have shipped with, including both
   * car consoles, so correcting it for everyone belongs in its own change with its own screenshots.
   */
  alignLabelsStart?: boolean;
}>;

const StarRating = ({ rating }: { rating: number }) => {
  const isHalfRating = rating % 1 === 0.5;
  return (
    <Flex columnGap={pxToRem(2)}>
      {Array.from({ length: Math.floor(rating) }, (_, index) => (
        <StarFilled
          size={16}
          key={index}
        />
      ))}
      {isHalfRating && <StarHalfFilled size={16} />}
    </Flex>
  );
};

StarRating.Skeleton = () => {
  return (
    <Flex columnGap={pxToRem(4)}>
      <Skeleton
        width={14}
        height={13}
      />
      <Skeleton
        width={14}
        height={13}
      />
      <Skeleton
        width={14}
        height={13}
      />
      <Skeleton
        width={14}
        height={13}
      />
      <Skeleton
        width={14}
        height={13}
      />
    </Flex>
  );
};

const UserRating = ({ rating, reviewCount, minReviewCount = DEFAULT_MIN_REVIEW_COUNT, alignLabelsStart = false }: UserRatingProps) => {
  const { t } = useTranslation('accommodationsConsole');
  const formatReviewCount = useReviewCountFormatter();

  const label = useMemo(() => {
    if (!rating) {
      return null;
    }
    const categoryTrans = t('listingsPanel', 'userRatings', 'category');
    switch (true) {
      case rating >= 9:
        return categoryTrans.wonderful();
      case rating >= 8.5:
        return categoryTrans.fabulous();
      case rating >= 8:
        return categoryTrans.veryGood();
      case rating >= 7:
        return categoryTrans.good();
      case rating >= 6:
        return categoryTrans.pleasant();
      default:
        return null;
    }
  }, [rating, t]);

  const reviewCountText = useMemo(() => {
    if (!reviewCount) {
      return null;
    }
    return t('listingsPanel', 'userRatings', 'reviewCounts')?.({ reviewCount: formatReviewCount(reviewCount) });
  }, [formatReviewCount, reviewCount, t]);

  if (!rating || !reviewCount || reviewCount < minReviewCount) {
    return null;
  }

  return (
    <Flex
      alignItems="center"
      flexDirection="row"
      columnGap={{ _: 3, sm2: 2 }}
      // The score badge is `flexShrink={0}` and the label is `nowrap`, so on a narrow card the two
      // could only overflow. Wrapping lets the label block drop BELOW the badge and take the column's
      // full width, which is the difference between a tight layout and text crossing the card border.
      //
      // This is the fallback the label needs, because the label itself cannot wrap: it is one word
      // in most locales ("Wonderful", "Ausgezeichnet"), and a line break needs a space. Room is the
      // only thing that helps it, and dropping to its own line is the only way to get more.
      //
      // Engages solely when the row does not fit, so every consumer with space is unaffected.
      flexWrap="wrap"
      rowGap={1}
    >
      {typeof rating === 'number' && rating > 0 && (
        <Flex
          color="white"
          flexShrink={0}
          alignItems="center"
          width={pxToRem(34)}
          height={pxToRem(34)}
          justifyContent="center"
          borderRadius={pxToRem(8)}
          backgroundColor="mono14"
        >
          <TextV2
            fontWeight={600}
            typographyVariant="label2"
          >
            {formatUserRatingNumber(rating)}
          </TextV2>
        </Flex>
      )}
      {(label || !!reviewCountText) && (
        <Flex
          rowGap={1}
          columnGap={2}
          alignItems={{ _: 'center', sm2: alignLabelsStart ? 'flex-start' : 'none' }}
          flexDirection={{ _: 'row', sm2: 'column' }}
          // Both children are `nowrap`, so in a container too narrow for "9.4 Wonderful (131
          // reviews)" on one line this block used to overflow its parent rather than give: inside a
          // bordered card that reads as text escaping the card. Wrapping only engages when the line
          // does not fit, so a consumer with room is unaffected; `minWidth` lets the block shrink
          // below its content width, which a flex item does not do by default.
          flexWrap="wrap"
          minWidth={0}
        >
          {label && (
            <TextV2
              whiteSpace="nowrap"
              typographyVariant="button1"
            >
              {label}
            </TextV2>
          )}
          {!!reviewCountText && (
            <>
              <TextV2
                color="mono10"
                whiteSpace="nowrap"
                typographyVariant="label2"
                display={{ _: 'inline', sm2: 'none' }}
              >
                ({reviewCountText})
              </TextV2>
              <TextV2
                color="mono10"
                whiteSpace="nowrap"
                typographyVariant="label2"
                display={{ _: 'none', sm2: 'inline' }}
              >
                {reviewCountText}
              </TextV2>
            </>
          )}
        </Flex>
      )}
    </Flex>
  );
};

UserRating.Skeleton = () => {
  return (
    <Flex
      alignItems="center"
      flexDirection="row"
      columnGap={{ _: 3, sm2: 2 }}
    >
      <Skeleton
        height={pxToRem(34)}
        width={pxToRem(34)}
      />
      <Flex
        flexDirection={{ _: 'row', sm2: 'column' }}
        alignItems={{ _: 'center', sm2: 'none' }}
        columnGap={2}
        rowGap={pxToRem(10)}
        // Mirrors `UserRating`'s own fix above. These bars are fixed-width, so in a container
        // narrower than their sum the skeleton overflowed the card while the loaded state wrapped —
        // a placeholder that misreports the shape of what is coming.
        flexWrap="wrap"
        minWidth={0}
      >
        <SkeletonText
          rows={1}
          width={75}
          skeletonHeight={20}
        />
        <SkeletonText
          rows={1}
          width={75}
          skeletonHeight={14}
        />
      </Flex>
    </Flex>
  );
};

export { UserRating, StarRating };
